// Round 123.5 — In-memory API cache + in-flight dedupe.
// Round 124.11 — SWR (stale-while-revalidate) + sessionStorage warm start.
//
// Problem: Every tab (Orders, Calendar, Customers, Finance, Dashboard)
// re-fetches its data on mount. When the user pings around the app,
// the same `/orders` list is fetched 5-10× in a minute, each response
// 1-3 MB on production. Widgets ALSO fetch independently (2 separate
// `/orders` hits inside DashboardWidgets alone).
//
// This helper wraps `api.get(url, params)` with:
//   • **Dedupe** — if a fetch for the same key is already in flight,
//     wait on the same Promise instead of firing a duplicate.
//   • **TTL cache** — after a fetch resolves, hold the result for
//     `ttlMs` (default 60 s).
//   • **SWR (stale-while-revalidate)** — when `opts.swr` is truthy
//     and we have a cached value (even if expired), return it
//     synchronously via a resolved Promise while a fresh fetch runs
//     in the background. Tab switches feel instant, and the UI
//     silently updates a moment later when the fresh data lands.
//   • **sessionStorage warm start** — hot keys are persisted so a
//     full page reload still boots with warm data instead of a
//     white "Loading…" state. Persistence is scoped to the current
//     browser tab so a fresh window still fetches fresh.
//   • **Invalidation** — call `invalidateCache(prefix)` to purge
//     entries whose key starts with `prefix`.
//
// Usage:
//   import { cachedGet, invalidateCache } from "../lib/apiCache";
//   const orders = await cachedGet("/orders");                   // 60 s cache
//   const orders = await cachedGet("/orders", null, { swr: true }); // instant + revalidate
//   invalidateCache("/orders");                                  // after a mutation
//
// Kept intentionally tiny — no eviction beyond TTL, no size cap.
// The set of hot keys is small (< 20) so the memory footprint is
// negligible.
import { api } from "./api";

const CACHE = new Map(); // key -> { data, expiresAt }
const IN_FLIGHT = new Map(); // key -> Promise
const SS_PREFIX = "apiCache::v1::";

function keyFor(url, params) {
  if (!params) return url;
  const parts = Object.keys(params)
    .sort()
    .map((k) => `${k}=${params[k] ?? ""}`)
    .join("&");
  return parts ? `${url}?${parts}` : url;
}

// ---- sessionStorage warm-start helpers ----
// Only the hottest keys (Orders, Customers, Dashboard summary) are
// worth persisting; everything else is skipped to keep parse cost
// low on reload. Persistence caps entries at ~2 MB each to avoid
// filling the ~5 MB tab quota with a single response.
const PERSIST_PREFIXES = ["/orders", "/customers", "/dashboard/summary"];
const PERSIST_MAX_BYTES = 2_000_000;

function shouldPersist(key) {
  return PERSIST_PREFIXES.some((p) => key === p || key.startsWith(`${p}?`));
}

function persist(key, entry) {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  if (!shouldPersist(key)) return;
  try {
    const raw = JSON.stringify(entry);
    if (raw.length > PERSIST_MAX_BYTES) return;
    window.sessionStorage.setItem(SS_PREFIX + key, raw);
  } catch {
    /* quota exceeded / disabled — silently skip */
  }
}

function hydrate(key) {
  if (typeof window === "undefined" || !window.sessionStorage) return null;
  if (!shouldPersist(key)) return null;
  try {
    const raw = window.sessionStorage.getItem(SS_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export async function cachedGet(url, params, opts = {}) {
  const { ttlMs = 60_000, force = false, swr = false } = opts;
  const key = keyFor(url, params);

  // Warm start: rehydrate once per key from sessionStorage so a full
  // page reload starts with immediate content instead of a spinner.
  if (!CACHE.has(key)) {
    const warm = hydrate(key);
    if (warm && warm.data !== undefined) CACHE.set(key, warm);
  }

  const now = Date.now();
  const hit = CACHE.get(key);
  const inflight = IN_FLIGHT.get(key);

  // Fresh cache — return immediately.
  if (!force && hit && hit.expiresAt > now) return hit.data;

  // Piggyback on an in-flight fetch.
  if (!force && inflight) {
    // SWR path: return the stale value now and let the caller ignore
    // this promise. If there's no stale value, they have to await.
    if (swr && hit) return hit.data;
    return inflight;
  }

  const startFetch = () => {
    const promise = (async () => {
      try {
        const { data } = await api.get(url, params ? { params } : undefined);
        const entry = { data, expiresAt: Date.now() + ttlMs };
        CACHE.set(key, entry);
        persist(key, entry);
        return data;
      } finally {
        IN_FLIGHT.delete(key);
      }
    })();
    IN_FLIGHT.set(key, promise);
    return promise;
  };

  // SWR path with a stale hit — kick a revalidation but resolve
  // synchronously with the stale value.
  if (swr && hit) {
    startFetch().catch(() => {});
    return hit.data;
  }

  return startFetch();
}

export function invalidateCache(prefix) {
  try { window.dispatchEvent(new CustomEvent("adams:cache-invalidate", { detail: prefix })); } catch { /* noop */ }
  const clearedKeys = [];
  if (!prefix) {
    for (const k of CACHE.keys()) clearedKeys.push(k);
    CACHE.clear();
  } else {
    for (const k of Array.from(CACHE.keys())) {
      if (k === prefix || k.startsWith(`${prefix}?`) || k.startsWith(`${prefix}/`)) {
        CACHE.delete(k);
        clearedKeys.push(k);
      }
    }
  }
  if (typeof window !== "undefined" && window.sessionStorage) {
    for (const k of clearedKeys) {
      try {
        window.sessionStorage.removeItem(SS_PREFIX + k);
      } catch {
        /* ignore */
      }
    }
  }
}

// Tab revisit: paint the last copy instantly (when `stale`), then always fetch fresh.
export async function swrGet(url, onData, stale = false) {
  const hit = stale === true ? CACHE.get(url) : null;
  if (hit) onData(hit.data);
  const data = await cachedGet(url, null, { force: true, ttlMs: 300_000 });
  onData(data);
  return data;
}

// Round 124.11 — Public prefetch helper. Fires GET into the cache
// without awaiting; callers use it right after login / route mount
// to warm the next likely tab so its first render is instant.
export function prefetch(url, params, opts) {
  cachedGet(url, params, opts).catch(() => {});
}
